import {
  canDecide,
  canDisburse,
  canRecordAppraisal,
  canRecordRecovery,
  isLive,
} from '@/features/loans/constants'
import { AppraisalForm, ApproveForm } from '@/features/loans/decision-forms'
import {
  DisburseForm,
  RecoveryForm,
  RepaymentForm,
  SettlementForm,
} from '@/features/loans/money-forms'
import { CycleOperationScreen } from '@/features/loans/operation-screen'
import { LOAN_PERMISSIONS } from '@/features/loans/permissions'
import {
  ClassificationOverrideForm,
  GuaranteeOffsetForm,
  RestructureForm,
  WaivePenaltyForm,
  WriteOffForm,
} from '@/features/loans/workout-forms'

/**
 * One screen per operation on a round.
 *
 * They are separate routes rather than dialogs on the round, and every one of them refuses below
 * the `lg` breakpoint. The mobile policy names disbursement, write-off and the security-deposit
 * offset outright; the rest are here on the same reasoning, stated once so it does not have to be
 * argued eleven times:
 *
 *   - **keying a money amount is what touch is worst at**, which covers the appraisal's
 *     recommendation, the approval, the repayment, the settlement, the waiver, the offset and the
 *     recovery; and
 *   - **a countersigned act should be done at a desk**, which covers the restructure and the
 *     classification override — both change what a member owes or how the institution reports
 *     them, and both are queued for somebody else to sign.
 *
 * What is left on the round itself is refusing an application and calling one off. Neither keys an
 * amount, neither is countersigned, and both are exactly the decision a manager should be able to
 * take from wherever they are.
 *
 * Each component below is a thin wrapper: the shell answers width, permission, load and state, in
 * that order, and hands the form the round.
 */

const KEYING_AN_AMOUNT =
  'It keys a cash amount against a real member’s account, and a mis-keyed amount is found at end-of-day reconciliation rather than at the counter.'

export function LoanAppraisalPage() {
  return (
    <CycleOperationScreen
      screenName="Recording an appraisal"
      refusalReason="It carries a recommended amount, and keying money is the one thing touch is worst at."
      title="Appraisal"
      description="The finding the credit decision will be made on."
      permission={LOAN_PERMISSIONS.appraise}
      appliesTo={canRecordAppraisal}
      appliesToDescription="An appraisal is recorded while a round is submitted or under appraisal. After a decision there is nothing left to appraise."
    >
      {(context) => <AppraisalForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanApprovalPage() {
  return (
    <CycleOperationScreen
      screenName="Approving a round"
      refusalReason="Approval freezes the terms at an amount keyed here, and it is countersigned."
      title="Approve the round"
      description="Freezes the terms at the approved amount."
      permission={LOAN_PERMISSIONS.approve}
      appliesTo={canDecide}
      appliesToDescription="A round is decided while it is submitted, under appraisal, or waiting for a countersignature."
    >
      {(context) => <ApproveForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanDisbursementPage() {
  return (
    <CycleOperationScreen
      screenName="Disbursement"
      refusalReason="Money is leaving the institution. That deserves a considered desk, not a bus."
      title="Release the money"
      description="Generates the schedule and posts to the ledger."
      permission={LOAN_PERMISSIONS.disburse}
      appliesTo={canDisburse}
      appliesToDescription="Money is released on a round that has been approved, or one whose disbursement is waiting for a countersignature."
    >
      {(context) => <DisburseForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanRepaymentPage() {
  return (
    <CycleOperationScreen
      screenName="Posting a repayment"
      refusalReason={KEYING_AN_AMOUNT}
      title="Post a repayment"
      description="Applied oldest instalment first, in the product's allocation order."
      permission={LOAN_PERMISSIONS.repaymentPost}
      appliesTo={isLive}
      appliesToDescription="Collections are posted against a live round. A round that is closed, refused or written off takes none."
    >
      {(context) => <RepaymentForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanSettlementPage() {
  return (
    <CycleOperationScreen
      screenName="Settling a round early"
      refusalReason={KEYING_AN_AMOUNT}
      title="Settle early"
      description="Clears the round at the quoted figure, rebating unearned interest where the product allows."
      permission={LOAN_PERMISSIONS.repaymentPost}
      appliesTo={isLive}
      appliesToDescription="A settlement clears a live round. There is nothing to settle on one that is already closed."
    >
      {(context) => <SettlementForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanPenaltyWaiverPage() {
  return (
    <CycleOperationScreen
      screenName="Waiving penalty"
      refusalReason="A debt is being forgiven, at an amount keyed by hand, and it is countersigned."
      title="Waive penalty"
      description="Reduces what the member owes. Nothing is posted."
      permission={LOAN_PERMISSIONS.penaltyWaive}
      appliesTo={isLive}
      appliesToDescription="Penalty is waived on a live round. Once a round is closed or written off there is nothing accruing to waive."
    >
      {(context) => <WaivePenaltyForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanRestructurePage() {
  return (
    <CycleOperationScreen
      screenName="Restructuring a round"
      refusalReason="It reschedules a member's whole debt and is countersigned. It needs the schedule in view."
      title="Restructure"
      description="Reschedules the unpaid balance. Posts nothing."
      permission={LOAN_PERMISSIONS.restructure}
      appliesTo={isLive}
      appliesToDescription="Only a live round can be rescheduled. There is no balance to reschedule on a closed or written-off one."
    >
      {(context) => <RestructureForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanWriteOffPage() {
  return (
    <CycleOperationScreen
      screenName="Writing a round off"
      refusalReason="A debt is being given up on. That deserves a considered desk, not a bus."
      title="Write off"
      description="Against the allowance, with the board decision that authorised it."
      permission={LOAN_PERMISSIONS.writeOff}
      appliesTo={isLive}
      appliesToDescription="A live round is written off. One that is already written off cannot be again."
    >
      {(context) => <WriteOffForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanGuaranteeOffsetPage() {
  return (
    <CycleOperationScreen
      screenName="Executing the group guarantee"
      refusalReason="One member's collateral is being taken for another's debt, at an amount keyed here."
      title="Execute the guarantee"
      description="Takes a member's security deposit against this round's arrears."
      permission={LOAN_PERMISSIONS.guaranteeOffset}
      appliesTo={isLive}
      appliesToDescription="The guarantee is executed against a live round that is in arrears."
    >
      {(context) => <GuaranteeOffsetForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanRecoveryPage() {
  return (
    <CycleOperationScreen
      screenName="Recording a recovery"
      refusalReason={KEYING_AN_AMOUNT}
      title="Record a recovery"
      description="Cash collected against a round that has already been written off."
      permission={LOAN_PERMISSIONS.repaymentPost}
      appliesTo={canRecordRecovery}
      appliesToDescription="A recovery applies only to a round that has been written off. Money against a live round is a repayment."
    >
      {(context) => <RecoveryForm {...context} />}
    </CycleOperationScreen>
  )
}

export function LoanClassificationOverridePage() {
  return (
    <CycleOperationScreen
      screenName="Overriding a classification"
      refusalReason="It changes what the institution reports about a facility, and it is countersigned."
      title="Override the classification"
      description="Sets the class by hand, over the one the run computed."
      permission={LOAN_PERMISSIONS.classificationOverride}
      appliesTo={isLive}
      appliesToDescription="A class is set on a live facility. A closed or written-off round is no longer classified."
    >
      {(context) => <ClassificationOverrideForm {...context} />}
    </CycleOperationScreen>
  )
}
